import { ColumnHelper } from "@tanstack/react-table";
import { ExperienceMultiToggle } from "PFApp/components/experience_multi_toggle";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { Checkbox } from "PFComponents/checkbox";
import { ExperienceLevel } from "PFComponents/experience_level";
import { Icon } from "PFComponents/icon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { SkillToRank } from "../../feedback_form_context";
import { useFeedbackFormMutations } from "../../hooks/use_feedback_form";

type UseSkillsTableColumns = {
  readOnly?: boolean;
};

const EXPERIENCE_OPTION_WIDTH = 80;

export const useSkillsTableColumns = ({ readOnly }: UseSkillsTableColumns) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.feedback" });
  const { handleRankChange, handleActivation } = useFeedbackFormMutations();
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();

  return useCallback(
    (columnHelper: ColumnHelper<SkillToRank>) => [
      columnHelper.accessor((row) => row.isActive, {
        id: "used",
        cell: (info) => {
          const isActive = !!info.getValue();
          if (readOnly) {
            return <Icon name="check" />;
          }
          return (
            <Checkbox checked={isActive} onChange={() => handleActivation(info.row.original, !isActive)} />
          );
        },
        header: () => t("headers.used"),
        enableSorting: false,
        size: 50
      }),
      columnHelper.accessor((row) => row.name, {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("headers.skill"),
        enableSorting: false
      }),
      columnHelper.accessor((row) => row.experience, {
        id: "experience",
        cell: (info) => {
          const experience = info.getValue();
          const skill = info.row.original;
          const skillId = skill.id || skill.globalId;

          if (readOnly) {
            return (
              <ExperienceLevel
                experienceLevels={experienceLevels}
                experienceLabelMap={experienceLabelMap}
                experience={experience}
              />
            );
          }

          return (
            <ExperienceMultiToggle
              experience={experience}
              showLabel={false}
              onChange={(newExperience) => handleRankChange(newExperience, skillId)}
              compact
            />
          );
        },
        header: () => t("headers.proficiency"),
        enableSorting: false,
        size: experienceLevels.length * EXPERIENCE_OPTION_WIDTH
      })
    ],
    [experienceLevels, experienceLabelMap, handleActivation, handleRankChange, readOnly, t]
  );
};
